import type {CarouselTabTiers, ExternalStockParams, SelectedFilter, SourceTier} from '@Panels/stock-panel/stock-panel.types';
import {AnimationSubFilter, IS_GETTY_ENABLED, PricingSubFilter, QualitySubFilter, StockButtonFilters, StockPanelTabs} from '@Panels/stock-panel/stock-panel.types';
import {StockSources} from '@Libraries/pmw-stock-media-library';
import {isUserStudent} from '@Utils/user.util';
import {getUserType} from '@Libraries/user.library';

const filterTieredSourcesByBlacklist = (tieredSources: SourceTier[], blacklistedSources?: StockSources[]): SourceTier[] => {
  if (!blacklistedSources || blacklistedSources.length === 0) {
    return tieredSources;
  }

  return tieredSources
    .map((tier) => {
      const filteredTier: SourceTier = {};

      if (tier.internalStock) {
        const filteredInternalSources = tier.internalStock.stockSources.filter((source) => !blacklistedSources.includes(source));
        if (filteredInternalSources.length > 0) {
          filteredTier.internalStock = {
            ...tier.internalStock,
            stockSources: filteredInternalSources,
          };
        }
      }

      if (tier.externalStock) {
        const filteredExternalStock = tier.externalStock.filter((externalStock) => !blacklistedSources.includes(externalStock.stockSource));
        if (filteredExternalStock.length > 0) {
          filteredTier.externalStock = filteredExternalStock;
        }
      }

      if (filteredTier.internalStock || (filteredTier.externalStock && filteredTier.externalStock.length > 0)) {
        return filteredTier;
      }
      return null;
    })
    .filter((tier): tier is SourceTier => tier !== null);
};

const isGettyAvailable = (): boolean => {
  return !isUserStudent(getUserType()) && IS_GETTY_ENABLED;
};

const isPixabayAvailable = (): boolean => {
  return !window.PMW.currentUserOnPageLoad?.isFreelancer;
};

const makeExternalStock = (source: StockSources, score: number): ExternalStockParams => {
  return {stockSource: source, score, offset: 0};
};

const getConditionalGettyStock = (source: StockSources, score: number): ExternalStockParams[] => {
  return isGettyAvailable() ? [makeExternalStock(source, score)] : [];
};

const getConditionalPixabayStock = (source: StockSources, score: number): ExternalStockParams[] => {
  return isPixabayAvailable() ? [makeExternalStock(source, score)] : [];
};

const getTieredSourcesForPhotosTab = (selectedFilters: SelectedFilter[], searchTerm?: string): SourceTier[] => {
  const pricingFilter = selectedFilters.find((f) => (f.filter as StockButtonFilters) === StockButtonFilters.PRICING);
  const pricingSubFilter = pricingFilter?.subFilter;

  const pmwStockImageInternal = searchTerm?.length === 0 ? {stockSources: [StockSources.PMW_STOCK_IMAGE], score: 100, offset: 0} : undefined;

  if (pricingSubFilter === PricingSubFilter.FREE) {
    return [
      {
        internalStock: pmwStockImageInternal,
        externalStock: [makeExternalStock(StockSources.STORYBLOCK_IMAGE, 100), ...getConditionalPixabayStock(StockSources.PIXABY_IMAGE, 100)],
      },
    ];
  }

  if (pricingSubFilter === PricingSubFilter.PREMIUM) {
    return [{externalStock: [...getConditionalGettyStock(StockSources.GETTY_IMAGE, 100)]}];
  }

  return [
    {
      internalStock: pmwStockImageInternal,
      externalStock: [
        makeExternalStock(StockSources.STORYBLOCK_IMAGE, 100),
        ...getConditionalPixabayStock(StockSources.PIXABY_IMAGE, 100),
        ...getConditionalGettyStock(StockSources.GETTY_IMAGE, 100),
      ],
    },
  ];
};

const getTieredSourcesForVideosTab = (selectedFilters: SelectedFilter[]): SourceTier[] => {
  const pricingFilter = selectedFilters.find((f) => (f.filter as StockButtonFilters) === StockButtonFilters.PRICING);
  const qualityFilter = selectedFilters.find((f) => (f.filter as StockButtonFilters) === StockButtonFilters.QUALITY);
  const pricingSubFilter = pricingFilter?.subFilter;
  const qualitySubFilter = qualityFilter?.subFilter;

  if (qualitySubFilter === QualitySubFilter.UHD) {
    return [{externalStock: [makeExternalStock(StockSources.STORYBLOCK_VIDEO, 100), ...getConditionalGettyStock(StockSources.GETTY_VIDEO, 100)]}];
  }
  if (pricingSubFilter === PricingSubFilter.FREE) {
    return [{externalStock: [makeExternalStock(StockSources.STORYBLOCK_VIDEO, 100)]}];
  }
  if (pricingSubFilter === PricingSubFilter.PREMIUM) {
    return [{externalStock: [...getConditionalGettyStock(StockSources.GETTY_VIDEO, 100)]}];
  }

  if (qualitySubFilter === QualitySubFilter.UHD && pricingSubFilter === PricingSubFilter.FREE) {
    return [{externalStock: [makeExternalStock(StockSources.STORYBLOCK_VIDEO, 100)]}];
  }

  if (qualitySubFilter === QualitySubFilter.UHD && pricingSubFilter === PricingSubFilter.PREMIUM) {
    return [{externalStock: [...getConditionalGettyStock(StockSources.GETTY_VIDEO, 100)]}];
  }

  return [{externalStock: [makeExternalStock(StockSources.STORYBLOCK_VIDEO, 100), ...getConditionalGettyStock(StockSources.GETTY_VIDEO, 100)]}];
};

const getTieredSourcesForStickersTab = (selectedFilters: SelectedFilter[], isManageappPage = false): SourceTier[] => {
  const pricingFilter = selectedFilters.find((f) => (f.filter as StockButtonFilters) === StockButtonFilters.PRICING);
  const animationFilter = selectedFilters.find((f) => (f.filter as StockButtonFilters) === StockButtonFilters.ANIMATION);
  const pricingSubFilter = pricingFilter?.subFilter;
  const animationSubFilter = animationFilter?.subFilter;

  if (isManageappPage) {
    return [{externalStock: [makeExternalStock(StockSources.GETTY_ILLUSTRATION, 100)]}];
  }

  if (pricingSubFilter === PricingSubFilter.FREE && animationSubFilter === AnimationSubFilter.ANIMATED) {
    return [{internalStock: {stockSources: [StockSources.PMW_ANIMATED_STICKER, StockSources.PMW_AI_ANIMATED_STICKER], score: 100, offset: 0}}];
  }

  if (pricingSubFilter === PricingSubFilter.FREE && animationSubFilter === AnimationSubFilter.STILL) {
    return [
      {internalStock: {stockSources: [StockSources.PMW_STILL_STICKER, StockSources.PMW_AI_STICKER], score: 150, offset: 0}},
      {externalStock: [makeExternalStock(StockSources.STORYBLOCK_ILLUSTRATION, 50)]},
      {externalStock: [...getConditionalPixabayStock(StockSources.PIXABY_ILLUSTRATION, 50)]},
    ];
  }

  if (pricingSubFilter === PricingSubFilter.PREMIUM && animationSubFilter === AnimationSubFilter.ANIMATED) {
    return [];
  }

  if (pricingSubFilter === PricingSubFilter.PREMIUM && animationSubFilter === AnimationSubFilter.STILL) {
    return [
      {internalStock: {stockSources: [StockSources.PMW_EXTRACTED_GETTY_STICKER], score: 100, offset: 0}},
      {externalStock: [...getConditionalGettyStock(StockSources.GETTY_ILLUSTRATION, 100)]},
    ];
  }

  if (pricingSubFilter === PricingSubFilter.FREE) {
    return [
      {
        internalStock: {
          stockSources: [StockSources.PMW_ANIMATED_STICKER, StockSources.PMW_AI_ANIMATED_STICKER, StockSources.PMW_STILL_STICKER, StockSources.PMW_AI_STICKER],
          score: 100,
          offset: 0,
        },
      },
      {externalStock: [makeExternalStock(StockSources.STORYBLOCK_ILLUSTRATION, 30)]},
      {externalStock: [...getConditionalPixabayStock(StockSources.PIXABY_ILLUSTRATION, 10)]},
    ];
  }

  if (pricingSubFilter === PricingSubFilter.PREMIUM) {
    return [
      {internalStock: {stockSources: [StockSources.PMW_EXTRACTED_GETTY_STICKER], score: 100, offset: 0}},
      {externalStock: [...getConditionalGettyStock(StockSources.GETTY_ILLUSTRATION, 100)]},
    ];
  }

  if (animationSubFilter === AnimationSubFilter.ANIMATED) {
    return [{internalStock: {stockSources: [StockSources.PMW_ANIMATED_STICKER, StockSources.PMW_AI_ANIMATED_STICKER], score: 100, offset: 0}}];
  }

  if (animationSubFilter === AnimationSubFilter.STILL) {
    return [
      {internalStock: {stockSources: [StockSources.PMW_STILL_STICKER, StockSources.PMW_AI_STICKER, StockSources.PMW_EXTRACTED_GETTY_STICKER], score: 100, offset: 0}},
      {externalStock: [...getConditionalGettyStock(StockSources.GETTY_ILLUSTRATION, 30)]},
      {externalStock: [...getConditionalPixabayStock(StockSources.PIXABY_ILLUSTRATION, 30)]},
      {externalStock: [makeExternalStock(StockSources.STORYBLOCK_ILLUSTRATION, 60)]},
    ];
  }

  return [
    {
      internalStock: {
        stockSources: [
          StockSources.PMW_ANIMATED_STICKER,
          StockSources.PMW_AI_ANIMATED_STICKER,
          StockSources.PMW_STILL_STICKER,
          StockSources.PMW_EXTRACTED_GETTY_STICKER,
          StockSources.PMW_AI_STICKER,
        ],
        score: 100,
        offset: 0,
      },
    },
    {externalStock: [...getConditionalGettyStock(StockSources.GETTY_ILLUSTRATION, 40)]},
    {externalStock: [makeExternalStock(StockSources.STORYBLOCK_ILLUSTRATION, 40)]},
    {externalStock: [...getConditionalPixabayStock(StockSources.PIXABY_ILLUSTRATION, 10)]},
  ];
};

const getTieredSourcesForIconsTab = (): SourceTier[] => {
  return [
    {
      internalStock: {
        stockSources: [StockSources.PMW_ICON, StockSources.PMW_AI_ICON],
        score: 100,
        offset: 0,
      },
    },
  ];
};

const getTieredSourcesForShapesTab = (): SourceTier[] => {
  return [
    {
      internalStock: {
        stockSources: [StockSources.PMW_SHAPE],
        score: 100,
        offset: 0,
      },
    },
  ];
};

const getTieredSourcesForAudiosTab = (): SourceTier[] => {
  return [{externalStock: [makeExternalStock(StockSources.STORYBLOCK_AUDIO, 100)]}];
};

export const getTieredSourcesForSelectedTab = (
  selectedTab: string,
  selectedFilters: SelectedFilter[],
  isManageappPage = false,
  blacklistedSources?: StockSources[]
): SourceTier[] => {
  const tab = selectedTab as StockPanelTabs;
  let tieredSources: SourceTier[] = [];

  switch (tab) {
    case StockPanelTabs.PHOTOS:
      tieredSources = getTieredSourcesForPhotosTab(selectedFilters);
      break;
    case StockPanelTabs.VIDEOS:
      tieredSources = getTieredSourcesForVideosTab(selectedFilters);
      break;
    case StockPanelTabs.STICKERS:
      tieredSources = getTieredSourcesForStickersTab(selectedFilters, isManageappPage);
      break;
    case StockPanelTabs.ICONS:
      tieredSources = getTieredSourcesForIconsTab();
      break;
    case StockPanelTabs.SHAPES:
      tieredSources = getTieredSourcesForShapesTab();
      break;
    case StockPanelTabs.AUDIOS:
      tieredSources = getTieredSourcesForAudiosTab();
      break;
    default:
      return [];
  }

  return filterTieredSourcesByBlacklist(tieredSources, blacklistedSources);
};

export const getCarouselTiersForTabs = (tabs: StockPanelTabs[], blacklistedSources?: StockSources[]): CarouselTabTiers => {
  const tabTiers: CarouselTabTiers = {};
  tabs.forEach((tab) => {
    let tiers: SourceTier[] = [];
    switch (tab) {
      case StockPanelTabs.PHOTOS:
        tiers = getTieredSourcesForPhotosTab([]);
        break;
      case StockPanelTabs.STICKERS:
        tiers = getTieredSourcesForStickersTab([]);
        break;
      case StockPanelTabs.ICONS:
        tiers = getTieredSourcesForIconsTab();
        break;
      case StockPanelTabs.SHAPES:
        tiers = getTieredSourcesForShapesTab();
        break;
      case StockPanelTabs.VIDEOS:
        tiers = getTieredSourcesForVideosTab([]);
        break;
      case StockPanelTabs.AUDIOS:
        tiers = getTieredSourcesForAudiosTab();
        break;
      default:
        break;
    }
    const filteredTiers = filterTieredSourcesByBlacklist(tiers, blacklistedSources);
    if (filteredTiers.length > 0) {
      tabTiers[tab] = filteredTiers;
    }
  });
  return tabTiers;
};

export const getTieredSourcesForAiAnimatedStickerImport = (): SourceTier[] => {
  return [
    {
      internalStock: {
        stockSources: [StockSources.PMW_STILL_STICKER, StockSources.PMW_AI_STICKER],
        score: 100,
        offset: 0,
      },
    },
  ];
};
